import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { usePublishScreenData } from "../utils/aiScreenContext";

/**
 * Cloud networking — Azure virtual networks, AWS VPCs, GCP VPC networks.
 *
 * READ-ONLY, and it says so. All three adapters can list networks and none
 * can change them, so this screen offers no buttons rather than buttons that
 * fail. If a write capability is added to an adapter later, the capability
 * registry will report it and this page is where it belongs.
 *
 * The three vendors' shapes differ (address prefixes and subnet names vs a
 * CIDR block vs a subnet mode), so each is mapped to the same two columns:
 * what the network is called, and what addresses it covers.
 */

type Platform = {
  platform: string;
  label: string;
  groups: { key: string; label: string; capabilities: { key: string; writes: boolean }[] }[];
};
type Connector = {
  id: number;
  platform: string;
  platform_label: string;
  name: string;
  enabled: boolean;
  customer_id: number;
};

const WIRING: Record<
  string,
  { path: string; name: (r: any) => string; addresses: (r: any) => string; extra: (r: any) => string }
> = {
  azure: {
    path: "virtual-networks",
    name: (r) => r.name,
    addresses: (r) => (r.address_prefixes || []).join(", ") || "—",
    extra: (r) =>
      [r.location, r.resource_group, (r.subnets || []).length ? `${r.subnets.length} subnet(s)` : null]
        .filter(Boolean)
        .join(" · "),
  },
  aws: {
    path: "vpcs",
    name: (r) => r.name || r.vpc_id || r.id,
    addresses: (r) => r.cidr_block || "—",
    extra: (r) => [r.state, r.is_default ? "default VPC" : null].filter(Boolean).join(" · "),
  },
  gcp: {
    path: "gcp-networks",
    name: (r) => r.name,
    addresses: (r) => r.ipv4_range || r.subnet_mode || "—",
    extra: (r) =>
      [r.subnet_mode ? `${r.subnet_mode} subnets` : null, r.description].filter(Boolean).join(" · "),
  },
};

export default function CloudNetwork() {
  const { currentCustomerId } = useCustomer();
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [selected, setSelected] = useState<number | "">("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/api/v1/cloud-connectors/supported-platforms"),
      api.get("/api/v1/cloud-connectors"),
    ])
      .then(([p, c]) => {
        setPlatforms(p.data || []);
        setConnectors(c.data || []);
      })
      .catch(() => setErr("Could not load cloud connectors."));
  }, []);

  const usable = useMemo(() => {
    const withNet = new Set(
      platforms.filter((p) => p.groups.some((g) => g.key === "network")).map((p) => p.platform),
    );
    return connectors.filter(
      (c) => c.enabled && withNet.has(c.platform) && (!currentCustomerId || c.customer_id === currentCustomerId),
    );
  }, [platforms, connectors, currentCustomerId]);

  const current = usable.find((c) => c.id === selected);
  const wiring = current ? WIRING[current.platform] : undefined;

  /* Publish the visible networks to the AI Assist.
   *
   * REPORTED alongside compute: "Networking isn't showing me much either."
   * The screen published nothing, so the assistant had no idea which networks
   * existed, let alone their address ranges. Reuses the WIRING accessors the
   * table renders with, so the model sees exactly what the operator sees -
   * normalised, because each platform names the address range differently
   * (address_prefixes / cidr_block / ipv4_range) and vendor identifiers do not
   * belong in AI output.
   */
  const aiItems = useMemo(
    () =>
      wiring
        ? rows.map((r) => ({
            name: wiring.name(r),
            addresses: wiring.addresses(r),
            details: wiring.extra(r),
          }))
        : [],
    [wiring, rows],
  );

  usePublishScreenData(
    "Cloud networking",
    aiItems,
    current
      ? `${rows.length} network(s) on the "${current.name}" connection. Read-only here.`
      : "No cloud connection selected yet.",
  );

  const load = useCallback(async () => {
    if (!current || !wiring) return;
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/api/v1/cloud-connectors/${current.id}/${wiring.path}`);
      setRows(r.data || []);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "Could not read that cloud.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [current, wiring]);

  useEffect(() => {
    if (selected) load();
  }, [selected, load]);

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Cloud networking</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Virtual networks across Azure, AWS and Google Cloud. Read-only — none of the three
          adapters can change network configuration.
        </p>
      </div>

      {err && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm px-4 py-2 rounded-lg">
          {err}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : "")}
          className="border dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
        >
          <option value="">Choose a connector…</option>
          {usable.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} — {c.platform_label}
            </option>
          ))}
        </select>
        {selected && (
          <button
            onClick={load}
            disabled={loading}
            className="text-xs border dark:border-gray-600 dark:text-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        )}
      </div>

      {usable.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-6 text-sm text-gray-600 dark:text-gray-300">
          No cloud connector with networking is set up
          {currentCustomerId ? " for this customer" : ""}.{" "}
          <Link to="/cloud" className="text-blue-600 dark:text-blue-400 hover:underline">
            Add one
          </Link>
          .
        </div>
      )}

      {current && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
          <table className="w-full text-sm md:min-w-[620px]">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Network</th>
                <th className="text-left px-3 py-2 font-medium">Addresses</th>
                <th className="text-left px-3 py-2 font-medium">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((r, i) => (
                <tr key={i} className="text-gray-900 dark:text-gray-100">
                  <td className="px-3 py-2 font-medium">{wiring!.name(r)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{wiring!.addresses(r)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                    {wiring!.extra(r)}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    className="px-3 py-6 text-center text-xs italic text-gray-500 dark:text-gray-400"
                    colSpan={3}
                  >
                    No networks reported.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
